import log from "loglevel";

interface LogEntry {
  level: string;
  message: string;
  timestamp: string;
  data: any[];
}

interface LogMetadata {
  generatedAt: string;
  totalEntries: number;
  timeRange: {
    start?: string;
    end?: string;
  };
}

interface LogDownloadData {
  metadata: LogMetadata;
  logs: LogEntry[];
}

interface LogCollectorOptions {
  maxEntries?: number;
  enableConsoleLogging?: boolean;
}

export class LogCollector {
  private logEntries: LogEntry[] = [];
  private maxEntries: number;
  private enableConsoleLogging: boolean;

  constructor(options: LogCollectorOptions = {}) {
    this.maxEntries = options.maxEntries || 1000;
    this.enableConsoleLogging = options.enableConsoleLogging !== false;

    // Set log level
    log.setLevel(log.levels.DEBUG);

    if (this.enableConsoleLogging) {
      this.interceptConsole();
    }
  }

  private interceptConsole(): void {
    const originalConsole = { ...console };

    console.log = (...args: any[]) => {
      this.storeLog("log", ...args);
      originalConsole.log(...args);
    };

    console.error = (...args: any[]) => {
      this.storeLog("error", ...args);
      originalConsole.error(...args);
    };

    console.warn = (...args: any[]) => {
      this.storeLog("warn", ...args);
      originalConsole.warn(...args);
    };

    console.info = (...args: any[]) => {
      this.storeLog("info", ...args);
      originalConsole.info(...args);
    };
  }

  private storeLog(level: string, ...args: any[]): void {
    this.logEntries.push({
      level,
      message: args.join(" "),
      timestamp: new Date().toISOString(),
      data: args,
    });

    // Keep only last maxEntries
    if (this.logEntries.length > this.maxEntries) {
      this.logEntries = this.logEntries.slice(-this.maxEntries);
    }
  }

  // Get logs for download - same interface as your original logger
  getLogsForDownload(): LogDownloadData {
    return {
      metadata: {
        generatedAt: new Date().toISOString(),
        totalEntries: this.logEntries.length,
        timeRange: {
          start: this.logEntries[0]?.timestamp,
          end: this.logEntries[this.logEntries.length - 1]?.timestamp,
        },
      },
      logs: this.logEntries,
    };
  }

  // Clear all logs
  clearLogs(): void {
    this.logEntries = [];
  }

  // Get current log entries
  get logListArray(): LogEntry[] {
    return [...this.logEntries];
  }

  // Legacy methods for compatibility
  setPreviousIndex(length: number): void {
    // No longer needed but kept for compatibility
  }

  getPreviousIndex(): number {
    return this.logEntries.length - 1;
  }
}

// Create a global instance
export const appLogger = new LogCollector({
  maxEntries: 1000,
  enableConsoleLogging: true,
});

// Make it globally accessible
if (typeof window !== "undefined") {
  (window as any).logger = appLogger;
}
